<script>
import { SancareOcticon } from '@sancare/typescript-commons'
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'

import { predictionModelTypeKey, predictionModelTypeKeys } from '@/misc/prediction'
import { CountryEnum, ModeEnum } from '@/store/modules/settings/types'

import { hasRejectedSuggestion, isFirstRejectedSuggestion, isSuggestionDisplayed, showMoreLabelKey, sortRejectedSuggestionsLast, suggestionKey } from './rejectedSuggestions'
import SuggestedChronicDiagnosis from './SuggestedChronicDiagnosis.vue'
import SuggestedDiagnosis from './SuggestedDiagnosis.vue'
import SvgSearchIcon from './SvgSearchIcon.vue'

export default {
  components: {
    'suggested-diagnosis': SuggestedDiagnosis,
    'suggested-chronic-diagnosis': SuggestedChronicDiagnosis,
    'sancare-octicon': SancareOcticon,
    'svg-search-icon': SvgSearchIcon,
  },
  props: {
    appSettings: { type: Object, required: true },
    suggestedDiagnoses: { type: Array, required: true },
    rumId: { type: Number, required: true },
    standardModeDiagnosisGroup: { type: Object, required: true },
    healthData: { type: Object, required: true },
    selection: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    mode: { type: String, required: true },
    suggestionJustifications: { type: Object, default: null },
    staySavedSearches: { type: Array, default: null },
    type: { type: String, default: null },
    readonly: { type: Boolean, required: true },
  },
  emits: ['make-selection', 'switch-display-mode'],
  data() {
    return {
      showMore: false,
      extendedSuggestions: [],
      processingDiagnoses: [],
      CountryEnum
    }
  },
  computed: {
    ...mapGetters(['flatSancareSettings']),
    ...mapState({
      pmsiCountry: (state) => state.settings.pmsi.country,
    }),
    iaEnabled() {
      return this.flatSancareSettings['enable_ai_suggestions']
    },
    orderedSuggestedDiagnoses() {
      const orderedSuggestions = _.map(this.suggestedDiagnoses, (suggestion) => {
        suggestion.diagnoses = _.orderBy(suggestion.diagnoses, ['excluded', 'level', 'predictions.confidence'], ['asc', 'desc', 'desc'])

        return suggestion
      })

      return _.orderBy(orderedSuggestions, ['isExcluded', 'level.max', 'confidence.max'], ['asc', 'desc', 'desc'])
    },
    hidesRejected() {
      return hasRejectedSuggestion(this.suggestedDiagnoses)
    },
    displayedSuggestedDiagnoses() {
      return sortRejectedSuggestionsLast(_.filter(this.orderedSuggestedDiagnoses, (suggestion) => {
        return isSuggestionDisplayed(suggestion, this.showMore) && !this.isChronicOnly(suggestion)
      }))
    },
    // Chronic DAS are suggested purely from the patient's history (backend's chronic_das CTE), so
    // they can carry the flag on any diagnosis of any DAS suggestion, wildcard child included —
    // flatten across all of them rather than assuming one suggestion per diagnosis.
    chronicSuggestions() {
      return _.filter(_.flatMap(this.suggestedDiagnoses, 'diagnoses'), 'isChronic')
    },
  },
  methods: {
    predictionModelTypeKey,
    predictionModelTypeKeys,
    isFirstRejectedSuggestion,
    showMoreLabelKey,
    suggestionKey,
    hasVisibleSuggestion() {
      return _.some(this.suggestedDiagnoses, (suggestion) => isSuggestionDisplayed(suggestion, this.showMore))
    },
    hasHiddenSuggestion() {
      return _.some(this.suggestedDiagnoses, (suggestion) => !isSuggestionDisplayed(suggestion, false))
    },
    isSelectedSuggestion(suggestion) {
      return Boolean(
        !_.isNull(this.selection)
          && this.selection.labelType === 'DAS'
          && this.selection.labelReference === suggestion.reference
          && _.isEqual(this.selection.savedSearchIds, _.map(suggestion.staySavedSearches, 'search.id').sort((a, b) => a - b))
      )
    },
    isExternalDiagnosis(diagnosis) {
      return ['external', 'unsure', 'prediction_unsure'].indexOf(diagnosis.codingOrigin) !== -1
    },
    isCurrentDiagnosis(diagnosis) {
      return _.some(
        this.standardModeDiagnosisGroup.associatedDiags,
        (das) => !das.disabled && das.associatedDiagnosis.reference === diagnosis.reference
      )
    },
    hasSuggestedDiagnosis(reference) {
      return _.some(this.suggestedDiagnoses, (suggestion) => reference.match(suggestion.reference))
    },
    // A suggestion with no search and no prediction only exists because of the patient's chronic
    // history — it's rendered exclusively via chronicSuggestions below, so it must be excluded here
    // to avoid also showing it as an empty "no justification" row.
    isChronicOnly(suggestion) {
      return !suggestion.searches.length && !suggestion.predictions.length && _.some(suggestion.diagnoses, 'isChronic')
    },
    hasRevaloSearch(suggestion) {
      return _.some(suggestion.searches, (search) => search.search.type === ModeEnum.OPTIMIZABLE)
    },
    getChronicJustification(chronicDiagnosis) {
      const chronicJustifications = this.suggestionJustifications?.[`DAS:${chronicDiagnosis.reference}`]?.chronicJustifications ?? []
      return chronicJustifications.map((justification) => ({
        id: justification.sancareId,
        rssId: justification.rssId,
        stayEnd: justification.endDate,
        stayDuration: justification.stayDuration,
      }))
    },
    async addChronicDiagnosis(suggestedDiagnosis) {
      if (this.processingDiagnoses.indexOf(suggestedDiagnosis.reference) !== -1) {
        return
      }
      this.processingDiagnoses.push(suggestedDiagnosis.reference)
      await this.$store.dispatch('addDiagnosis', {
        rumId: this.rumId,
        diagnosisType: 'DAS',
        reference: suggestedDiagnosis.reference,
        justifications: null,
      })
      _.remove(this.processingDiagnoses, (diag) => diag === suggestedDiagnosis.reference)
    },
    async addDiagnosis(suggestedDiagnosis) {
      if (this.processingDiagnoses.indexOf(suggestedDiagnosis.reference) !== -1) {
        return
      }
      this.processingDiagnoses.push(suggestedDiagnosis.reference)
      await this.$store.dispatch('addDiagnosis', {
        rumId: this.rumId,
        diagnosisType: 'DAS',
        reference: suggestedDiagnosis.reference,
        justifications: null,
      })
      _.remove(this.processingDiagnoses, (diag) => diag === suggestedDiagnosis.reference)
    },
    getSearchesIds(diagnosis) {
      return _.map(diagnosis.staySavedSearches, (ss) => ss.search.id)
    },
    stringifySuggestionSearches(suggestion, sep, cut = false) {
      const content = _.map([...suggestion.staySavedSearches], (savedSearch) => savedSearch.search.name).join(sep)
      if (cut) {
        return content.substring(0, 20)
      }
      return content
    },
    toggleSuggestionDiagnoses(suggestion) {
      if (suggestion.isWildcard) {
        if (this.isExtendedSuggestion(suggestion)) {
          this.extendedSuggestions = _.filter(this.extendedSuggestions, (key) => key !== suggestionKey(suggestion))
        } else {
          this.extendedSuggestions.push(suggestionKey(suggestion))
        }
      }
    },
    isExtendedSuggestion(suggestion) {
      return this.extendedSuggestions.indexOf(suggestionKey(suggestion)) !== -1
    },
    makeSelection(selection) {
      this.$emit('make-selection', selection)
    },
    switchDisplayMode() {
      this.$emit('switch-display-mode')
    },
    showMoreDiags() {
      this.showMore = !this.showMore
    },
  }
}
</script>

<template>
  <div class="diagnosis-suggestions-card">
    <div class="card-body px-0 pt-0">
      <h1 class="card-title">
        {{ $t('diagnosis.'+type) }} <small class="fs-6">- {{ $t('stayDisplayer.sancareSuggestion', displayedSuggestedDiagnoses.length) }}</small>
        <span
          v-tooltip="'Afficher en mode cartes'"
          class="float-end"
          @click="switchDisplayMode()"
        >
          <sancare-octicon
            name="versions"
            class="cp"
            :width="30"
            :height="30"
          />
        </span>
      </h1>
    </div>
    <div
      v-for="(suggestion, suggestionIdx) in displayedSuggestedDiagnoses"
      :key="suggestionKey(suggestion)"
    >
      <div
        v-if="isFirstRejectedSuggestion(displayedSuggestedDiagnoses, suggestionIdx)"
        class="rejected-suggestions-separator"
      />
      <div class="row card-body px-0">
        <suggested-diagnosis
          :app-settings="appSettings"
          :type="type"
          :is-loading="isLoading"
          :is-wildcard="suggestion.isWildcard"
          :selected="selection !== null && selection.labelType === type && selection.labelReference === suggestion.reference"
          :suggested-diagnosis="suggestion.diagnoses[0]"
          :health-data="healthData"
          :suggestion-justifications="suggestionJustifications"
          :ia-enabled="iaEnabled"
          class="col-12"
          @make-selection="makeSelection"
        >
          <div
            class="row g-0 py-0 align-items-center diagnosis-item"
            :class="{ excluded: suggestion.isExcluded }"
            @click="toggleSuggestionDiagnoses(suggestion)"
          >
            <div class="col-2">
              <div
                v-if="suggestion.isWildcard"
                title="Voir les diagnostics correspondants"
                class="predicted-diagnosis-adder"
              >
                <sancare-octicon
                  :name="isExtendedSuggestion(suggestion) ? 'diff-removed' : 'diff-added'"
                  :width="18"
                  :height="18"
                />
              </div>
              <div
                v-else-if="!isCurrentDiagnosis(suggestion.diagnoses[0]) && !readonly"
                :id="'add-diag-btn-'+suggestion.reference"
                title="Ajouter le diagnostic"
                class="predicted-diagnosis-adder"
                @click.stop="addDiagnosis(suggestion.diagnoses[0])"
              >
                <div
                  v-if="processingDiagnoses.indexOf(suggestion.diagnoses[0].reference) !== -1"
                  class="loader loader-sm"
                />
                <sancare-octicon
                  v-else
                  name="arrow-left"
                  :width="20"
                  :height="20"
                />
              </div>
            </div>
            <div class="col-9 row g-0 align-items-center">
              <div
                :data-reference="suggestion.reference"
                :title="suggestion.description"
                class="col-5"
              >
                <div class="diagnosis-reference">
                  <h2>
                    {{ suggestion.reference }}
                  </h2>
                </div>
                <div
                  v-if="pmsiCountry === CountryEnum.FR"
                  class="mt-1 diagnosis-level"
                >
                  <span v-if="!suggestion.isExcluded">{{ $t('diagnosis.level') }}. {{ suggestion.level.label }}</span>
                  <span v-else>{{ $t('diagnosis.excluded') }}</span>
                </div>
              </div>
              <div class="col-6">
                <div
                  v-if="suggestion.searches.length"
                  v-tooltip="stringifySuggestionSearches(suggestion.diagnoses[0], ',\n')"
                  class="row g-0 align-items-center"
                >
                  <div class="col-3">
                    <svg-search-icon
                      :is-revalo="hasRevaloSearch(suggestion)"
                    />
                  </div>
                  <div class="col-8 ms-1 diagnosis-searches">
                    {{ stringifySuggestionSearches(suggestion.diagnoses[0], ', ', true) }}
                  </div>
                </div>
                <div
                  v-for="modelKey in predictionModelTypeKeys(suggestion.predictions)"
                  :key="modelKey"
                  class="row g-0 align-items-center"
                  :class="{ 'ia-disabled-line': !iaEnabled }"
                >
                  <div class="col-3">
                    <sancare-octicon
                      name="robot"
                      :width="18"
                    />
                  </div>
                  <div
                    v-tooltip="$t(modelKey)"
                    class="col-8 ms-1 diagnosis-confidence text-truncate"
                  >
                    {{ $t(modelKey) }}
                  </div>
                </div>
              </div>
              <div class="col-1">
                <div
                  v-if="suggestion.diagnoses[0] && suggestion.diagnoses[0].canShowRational && !suggestion.isWildcard && !readonly"
                  :class="{ selected: isSelectedSuggestion(suggestion.diagnoses[0]) }"
                  class="btn-select-diagnosis icon-block"
                  @click.stop="makeSelection({ labelType: 'DAS', labelReference: suggestion.reference, savedSearchIds: getSearchesIds(suggestion.diagnoses[0]) })"
                >
                  <sancare-octicon
                    name="note"
                    :width="18"
                    :height="18"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="suggestion.hasRejectedEvaluation"
              class="col-1 d-flex align-items-center"
            >
              <sancare-octicon
                name="blocked"
                class="rejected-suggestion-icon"
                :tooltip-text="$t('stayDisplayer.rejectedSuggestion')"
                :width="16"
                :height="16"
              />
            </div>
          </div>
        </suggested-diagnosis>
      </div>
      <div v-if="suggestion.isWildcard && suggestion.diagnoses.length > 1">
        <div v-show="isExtendedSuggestion(suggestion)">
          <div
            v-for="diagnosis in suggestion.diagnoses"
            :key="`suggested_diagnosis_${type}_${diagnosis.reference}`"
            class="row card-body px-0"
          >
            <suggested-diagnosis
              :app-settings="appSettings"
              :type="type"
              :is-loading="isLoading"
              :is-wildcard="false"
              :selected="selection !== null && selection.labelType === type && selection.labelReference === diagnosis.reference"
              :suggested-diagnosis="diagnosis"
              :health-data="healthData"
              :suggestion-justifications="suggestionJustifications"
              :ia-enabled="iaEnabled"
              class="col-12"
              @make-selection="makeSelection"
            >
              <div
                :class="{ excluded: suggestion.isExcluded }"
                class="row g-0 align-items-center diagnosis-item"
              >
                <!-- @click="/toggleDiagCategory(sDiag)" -->
                <div class="col-2">
                  <div
                    v-if="!isCurrentDiagnosis(diagnosis) && !readonly"
                    title="Ajouter le diagnostic"
                    class="predicted-diagnosis-adder"
                    @click.stop="addDiagnosis(diagnosis)"
                  >
                    <div
                      v-if="processingDiagnoses.indexOf(diagnosis.reference) !== -1"
                      class="loader loader-sm"
                    />
                    <sancare-octicon
                      v-else
                      name="arrow-left"
                      :width="20"
                      :height="20"
                    />
                  </div>
                </div>
                <div class="col-9 row g-0 align-items-center">
                  <div
                    :data-reference="diagnosis.reference"
                    :title="diagnosis.description"
                    class="col-5"
                  >
                    <h2 class="diagnosis-reference">
                      {{ diagnosis.reference }}
                    </h2>
                  </div>
                  <div class="col-6">
                    <div
                      v-if="diagnosis.staySavedSearches.length"
                      v-tooltip="stringifySuggestionSearches(diagnosis, ',\n')"
                      class="row g-0 align-items-center"
                    >
                      <div class="col-3">
                        <svg-search-icon
                          :is-revalo="hasRevaloSearch(suggestion)"
                        />
                      </div>
                      <div class="col-8 ms-1 diagnosis-searches">
                        {{ stringifySuggestionSearches(diagnosis, ', ', true) }}
                      </div>
                    </div>
                    <div
                      v-if="diagnosis.prediction"
                      class="row g-0 align-items-center"
                      :class="{ 'ia-disabled-line': !iaEnabled }"
                    >
                      <div class="col-3">
                        <sancare-octicon
                          name="robot"
                          :width="18"
                        />
                      </div>
                      <div
                        v-if="predictionModelTypeKey(diagnosis.prediction)"
                        v-tooltip="$t(predictionModelTypeKey(diagnosis.prediction))"
                        class="col-8 ms-1 diagnosis-confidence text-truncate"
                      >
                        {{ $t(predictionModelTypeKey(diagnosis.prediction)) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-1">
                  <div
                    v-if="diagnosis.canShowRational"
                    :class="{ selected: isSelectedSuggestion(diagnosis) }"
                    class="btn-select-diagnosis icon-block"
                    @click.stop="makeSelection({ labelType: 'DAS', labelReference: diagnosis.reference, savedSearchIds: getSearchesIds(diagnosis) })"
                  >
                    <sancare-octicon
                      name="note"
                      :width="18"
                      :height="18"
                    />
                  </div>
                </div>
              </div>
            </suggested-diagnosis>
          </div>
        </div>
      </div>
    </div>
    <div
      v-for="chronicDiagnosis in chronicSuggestions"
      :key="chronicDiagnosis.reference"
    >
      <div class="row card-body px-0">
        <suggested-chronic-diagnosis
          :chronic-suggestion="chronicDiagnosis"
          :chronic-justifications="getChronicJustification(chronicDiagnosis)"
          class="col-12"
        >
          <div class="row g-0 py-0 align-items-center diagnosis-item">
            <div class="col-2">
              <div
                title="Ajouter le diagnostic"
                class="predicted-diagnosis-adder"
                @click.stop="addChronicDiagnosis(chronicDiagnosis)"
              >
                <div
                  v-if="processingDiagnoses.indexOf(chronicDiagnosis.reference) !== -1"
                  class="loader loader-sm"
                />
                <sancare-octicon
                  v-else
                  name="arrow-left"
                  :width="20"
                  :height="20"
                />
              </div>
            </div>
            <div
              class="col-9 row g-0 align-items-center"
            >
              <div
                :data-reference="chronicDiagnosis.reference"
                :title="chronicDiagnosis.description"
                class="col-5"
              >
                <div class="diagnosis-reference">
                  <h2>
                    {{ chronicDiagnosis.reference }}
                  </h2>
                </div>
                <div
                  v-if="pmsiCountry === CountryEnum.FR"
                  class="mt-1 diagnosis-level"
                >
                  <span>{{ $t('diagnosis.level') }} {{ chronicDiagnosis.level }}</span>
                </div>
              </div>
              <div class="col-6">
                <div
                  class="row g-0 align-items-center"
                >
                  <div
                    class="col-3"
                    title="DAS Chronique"
                  >
                    <sancare-octicon
                      name="stopwatch"
                      :width="14"
                      :height="14"
                    />
                  </div>
                  <div class="col-8 ms-1 diagnosis-confidence" />
                </div>
              </div>
            </div>
          </div>
        </suggested-chronic-diagnosis>
      </div>
    </div>
    <div
      v-if="isLoading && !suggestedDiagnoses.length && !chronicSuggestions.length"
      class="loader loader-lg loader-center"
    />
    <div
      v-else-if="!suggestedDiagnoses.length && !chronicSuggestions.length"
      class="card-body px-0 text"
    >
      Aucun diagnostic detecté
    </div>
    <div
      v-else-if="!hasVisibleSuggestion() && !chronicSuggestions.length"
      class="card-body text-center"
    >
      Aucun diagnostic pertinent
    </div>
    <div
      v-if="hasHiddenSuggestion()"
      class="col-12 my-1 py-0 text-center"
    >
      <span
        :id="'show-more-'+type"
        class="btn-more-diagnosis"
        @click="showMoreDiags"
      >
        {{ $t(showMoreLabelKey(hidesRejected, showMore)) }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-title {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}
</style>
